import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface EncryptionState {
  // User encryption status
  isEncryptionEnabled: boolean;
  masterKeyUnlocked: boolean;

  // Master key (in memory, never persisted)
  masterKey: CryptoKey | null;

  // Private key (decrypted, in memory only)
  privateKey: CryptoKey | null;

  // User's public key (can be persisted)
  publicKey: string | null;

  // Salt for master key derivation
  salt: string | null;

  // PBKDF2 iterations
  iterations: number;

  // Session: track if user needs to unlock
  needsUnlock: boolean;

  // Actions
  setEncryptionEnabled: (enabled: boolean) => void;
  setMasterKey: (key: CryptoKey | null) => void;
  setPrivateKey: (key: CryptoKey | null) => void;
  setPublicKey: (key: string | null) => void;
  setSalt: (salt: string | null) => void;
  setIterations: (iterations: number) => void;
  setNeedsUnlock: (needs: boolean) => void;
  lockEncryption: () => void;
  unlockEncryption: (masterKey: CryptoKey, privateKey: CryptoKey) => void;
  reset: () => void;
}

/**
 * Encryption state store.
 * Note: Sensitive keys (masterKey, privateKey) are NOT persisted.
 * Only metadata is persisted to localStorage.
 */
export const useEncryptionStore = create<EncryptionState>()(
  persist(
    (set) => ({
      isEncryptionEnabled: false,
      masterKeyUnlocked: false,
      masterKey: null,
      privateKey: null,
      publicKey: null,
      salt: null,
      iterations: 100000,
      needsUnlock: false,

      setEncryptionEnabled: (enabled) =>
        set({ isEncryptionEnabled: enabled }),

      setMasterKey: (key) =>
        set({ masterKey: key, masterKeyUnlocked: !!key }),

      setPrivateKey: (key) => set({ privateKey: key }),

      setPublicKey: (key) => set({ publicKey: key }),

      setSalt: (salt) => set({ salt }),

      setIterations: (iterations) => set({ iterations }),

      setNeedsUnlock: (needs) => set({ needsUnlock: needs }),

      lockEncryption: () =>
        set({
          masterKey: null,
          privateKey: null,
          masterKeyUnlocked: false,
          needsUnlock: true,
        }),

      unlockEncryption: (masterKey, privateKey) =>
        set({
          masterKey,
          privateKey,
          masterKeyUnlocked: true,
          needsUnlock: false,
        }),

      reset: () =>
        set({
          isEncryptionEnabled: false,
          masterKeyUnlocked: false,
          masterKey: null,
          privateKey: null,
          publicKey: null,
          salt: null,
          iterations: 100000,
          needsUnlock: false,
        }),
    }),
    {
      name: 'encryption-storage',
      // Only persist metadata, NEVER persist keys
      partialize: (state) => ({
        isEncryptionEnabled: state.isEncryptionEnabled,
        publicKey: state.publicKey,
        salt: state.salt,
        iterations: state.iterations,
        needsUnlock: state.needsUnlock,
      }),
    }
  )
);
